import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import NotificationsBell from '../notifications/NotificationsBell';
import MobileDrawer from '../mobileDrawer/MobileDrawer';
import axiosConn from '../../axiosConn';
import useClickOutside from '../../clickOutside';
import { Search } from '@material-ui/icons';
import PlayerSearchResults from '../playerSearchResults/PlayerSearchResults';
import './trailblazerNav.css';

const TrailblazerNav = () => {
  const { player } = useContext(AuthContext);
  const showPresenceDirectoryLink = true; // Player-facing feature

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Search state
  const searchInput = useRef(null);
  const searchMenuRef = useRef(null);
  const [searchedPlayers, setSearchedPlayers] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const toggleDrawer = () => setDrawerOpen((v) => !v);

  const Logoff = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  // Unread notifications count (for MobileDrawer parity)
  useEffect(() => {
    let mounted = true;
    const fetchUnreadCount = async () => {
      try {
        const { data } = await axiosConn.get('notifications/my/unread-count', {
          headers: { Authorization: `Bearer ${player?.token}` },
        });
        if (mounted) {
          setUnreadCount(Number(data?.data?.count || data?.count || 0));
        }
      } catch {
        if (mounted) setUnreadCount(0);
      }
    };
    if (player?.token) fetchUnreadCount();
    return () => {
      mounted = false;
    };
  }, [player?.token]);

  const handleSearch = async (value) => {
    if (!value) {
      setShowSearchResults(false);
      setSearchedPlayers([]);
      return;
    }
    setShowSearchResults(true);
    try {
      const res = await axiosConn.get(
        `/players/regex/${encodeURIComponent(value)}`,
        {
          headers: { Authorization: `Bearer ${player?.token}` },
        },
      );
      setSearchedPlayers(res?.data?.data?.data || []);
    } catch {
      setSearchedPlayers([]);
    }
  };

  useClickOutside(searchMenuRef, () => {
    setShowSearchResults(false);
    if (searchInput.current) searchInput.current.value = '';
  });

  const profileImage =
    player?.data?.player?.profilePhoto ||
    'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';

  const playerId = player?.data?.player?.playerId || '';
  const jgk = Number(player?.data?.player?.jgkTokens || 0).toFixed(2);

  return (
    <>
      {/* Scoped nav */}
      <nav className="legacy-nav trailblazer-nav">
        <div className="nav-container">
          {/* Mobile view (mirror LegacyNav layout: logo left, bell center, hamburger right) */}
          <div className="nav-mobile-row">
            <Link to="/hunts" className="nav-brand nav-slot-left">
              <img
                src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png"
                alt="JG Logo"
                className="topbarJGLogoImgMobile"
              />
              <span
                className="brand-text"
                style={{
                  marginLeft: 8,
                  whiteSpace: 'nowrap',
                  color: '#facc15',
                  fontWeight: 900,
                }}
              >
                JG Trailblazer
              </span>
            </Link>

            <div className="nav-slot-center">
              <NotificationsBell className="topbar-bell" />
            </div>

            <button
              className="menu-toggle nav-slot-right"
              onClick={toggleDrawer}
              aria-label="Open menu"
            >
              ☰
            </button>
          </div>

          {/* Desktop brand */}
          <Link
            to="/"
            className="nav-brand desktop-only"
            style={{ whiteSpace: 'nowrap' }}
          >
            <img
              src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png"
              alt="JG Logo"
              className="topbarJGLogoImgTrailblazer"
            />
            <span
              className="brand-text"
              style={{
                marginLeft: 8,
                whiteSpace: 'nowrap',
                color: '#facc15',
                fontWeight: 900,
              }}
            >
              JG Trailblazer
            </span>
          </Link>

          {/* Desktop search */}
          <div className="topbarCenter desktop-only">
            <div className="searchbar">
              <Search className="searchIcon" />
              <label className="labelSearch" htmlFor="trailblazer-search">
                <input
                  id="trailblazer-search"
                  type="search"
                  autoComplete="on"
                  aria-label="Search"
                  placeholder="Search for players"
                  className="searchInput"
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchInput}
                />
              </label>
              {showSearchResults && searchedPlayers.length > 0 && (
                <div className="playerSearchResults" ref={searchMenuRef}>
                  <PlayerSearchResults
                    searchedPlayers={searchedPlayers}
                    onPlayerClick={() => setShowSearchResults(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Desktop hamburger (for consistency with LegacyNav) */}
          <button
            className="menu-toggle desktop-only"
            onClick={toggleDrawer}
            aria-label="Open menu"
          >
            ☰
          </button>

          {/* Desktop right side */}
          <div className="nav-right desktop-only">
            <Link
              className="nav-profile"
              to={playerId ? `/profile/${playerId}` : '/profile'}
            >
              <img src={profileImage} alt="avatar" className="nav-avatar" />
              <span className="nav-profile-label">Profile</span>
            </Link>

            <Link className="nav-link" to="/hunts">
              Hunts
            </Link>
            <Link className="nav-link" to="/business-directory">
              Businesses
            </Link>
            <Link className="nav-link" to="/leaderboard">
              Leaderboard
            </Link>
            <Link className="nav-link" to="/rewards">
              Rewards
            </Link>
            <Link className="nav-link" to="/social-share">
              Share
            </Link>
            <Link className="nav-link" to="/referrals">
              Referrals
            </Link>
            {showPresenceDirectoryLink && (
              <Link className="nav-link" to="/presence-directory">
                Presence Directory
              </Link>
            )}

            <NotificationsBell className="topbar-bell" />

            <div className="nav-end">
              <Link className="nav-jgk" to="/jgktoken">
                💰 {jgk} JGK
              </Link>
              <button
                className="nav-logout"
                onClick={() => {
                  if (window.confirm('Ready to blaze out? 🚪')) {
                    Logoff();
                  }
                }}
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <MobileDrawer
        show={drawerOpen}
        onClose={toggleDrawer}
        player={player}
        unreadCount={unreadCount}
      />
    </>
  );
};
export default TrailblazerNav;
